/**
 * Sign-in state for the Login and callback screens. The work is done by the
 * data source's AuthPort: live, a six-digit code emailed by Welthra V2, as in
 * the iPhone app; the demo, an instant link. No phone or SMS codes (AGENTS.md).
 */
import { useState } from "react";
import { useDataSource } from "@/data/DataProvider";
import type { CallbackResult } from "@/data/DataSource";
import { rememberNext } from "./nextPath";

export type { CallbackResult };

export function useMagicLink() {
  const { auth } = useDataSource();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMagicLink = async (email: string, next?: string | null): Promise<boolean> => {
    setLoading(true);
    setError(null);
    rememberNext(next);
    try {
      const result = await auth.sendMagicLink(email, next);
      if (!result.ok) {
        setError(result.error);
        return false;
      }
      return true;
    } finally {
      setLoading(false);
    }
  };

  const verifyCallback = async (): Promise<CallbackResult> => {
    setLoading(true);
    setError(null);
    try {
      const result = await auth.verifyCallback(new URLSearchParams(window.location.search));
      if (!result.ok) setError(result.message);
      return result;
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (email: string, code: string): Promise<boolean> => {
    if (!auth.verifyCode) return false;
    setLoading(true);
    setError(null);
    try {
      const result = await auth.verifyCode(email, code);
      if (!result.ok) {
        setError(result.error);
        return false;
      }
      return true;
    } finally {
      setLoading(false);
    }
  };

  return { sendMagicLink, verifyCallback, verifyCode, codeSignIn: !!auth.verifyCode, loading, error, setError };
}
